/**
 * ChatPopup Component — "The Guide" V3 (Decoupled Stage/UI)
 * 
 * Objectives:
 * 1. Stage/UI Separation: Visuals (BG, Pedestal, Character) are decoupled from interactive UI.
 * 2. Root Overflow: Pedestal (400% width) extends past all internal panel boundaries.
 * 3. Perfect Grounding: Pedestal surface aligned exactly to character feet.
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useChat } from '../contexts/ChatContext';
import { BookOpen, Feather, Plus, X, Download, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Pulse } from './Animations';

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

const GUIDE_POSES = {
    t1: { welcome: '/images/guide/t1/welcome.png', thinking: '/images/guide/t1/thinking.png', explaining: '/images/guide/t1/explaining.png', concerned: '/images/guide/t1/concerned.png', neutral: '/images/guide/t1/neutral.png', happy: '/images/guide/t1/welcome.png' },
    t2: { welcome: '/images/guide/t2/welcome.png', thinking: '/images/guide/t2/thinking.png', explaining: '/images/guide/t2/explaining.png', concerned: '/images/guide/t2/concerned.png', neutral: '/images/guide/t2/neutral.png', happy: '/images/guide/t2/welcome.png' },
    t3: { welcome: '/images/guide/t3/welcome.png', thinking: '/images/guide/t3/thinking.png', explaining: '/images/guide/t3/explaining.png', concerned: '/images/guide/t3/concerned.png', neutral: '/images/guide/t3/neutral.png', happy: '/images/guide/t3/welcome.png' },
    t4: { welcome: '/images/guide/t4/welcome.png', thinking: '/images/guide/t4/thinking.png', explaining: '/images/guide/t4/explaining.png', concerned: '/images/guide/t4/concerned.png', neutral: '/images/guide/t4/neutral.png', happy: '/images/guide/t4/welcome.png' },
    t5: { welcome: '/images/guide/t5/welcome.png', thinking: '/images/guide/t5/thinking.png', explaining: '/images/guide/t5/explaining.png', concerned: '/images/guide/t5/concerned.png', neutral: '/images/guide/t5/neutral.png', happy: '/images/guide/t5/welcome.png' },
};

const TIER_NAMES = { t1: 'Curious', t2: 'Comfortable', t3: 'Practiced', t4: 'Experienced', t5: 'Expert' };

// ============================================================================
// STAGE COMPONENT - The Visual Overlay (Layer 0)
// ============================================================================
function Stage({ mood = 'welcome', tier = 't4' }) {
    const [currentSprite, setCurrentSprite] = useState(null);
    const [opacityDip, setOpacityDip] = useState(false);
    const [isTierChange, setIsTierChange] = useState(false);
    const prevTierRef = useRef(tier);

    const targetSprite = (GUIDE_POSES[tier] || GUIDE_POSES.t4)[mood] || '/guide-succubus.png';

    useEffect(() => {
        if (prevTierRef.current !== tier) {
            setIsTierChange(true);
            prevTierRef.current = tier;
            setTimeout(() => setIsTierChange(false), 150);
        }
    }, [tier]);

    useEffect(() => {
        if (!currentSprite) {
            setCurrentSprite(targetSprite);
            return;
        }
        if (targetSprite !== currentSprite) {
            setOpacityDip(true);
            setTimeout(() => {
                setCurrentSprite(targetSprite);
                setOpacityDip(false);
            }, 75);
        }
    }, [targetSprite, currentSprite]);

    return (
        <div className="absolute inset-0 z-10 pointer-events-none overflow-visible">
            {/* 1. Background image (Dungeon) - inside the frame */}
            <div className="absolute top-0 bottom-0 left-0 w-52 overflow-hidden border-r border-wine-900/40">
                <img src="/images/guide/dungeon-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950 via-charcoal-950/40 to-charcoal-950" />
            </div>

            {/* 2. Visual Unit (Character Only) - Centered on the left panel (w-52) */}
            <div className="absolute bottom-0 left-0 w-52 h-0 flex items-end justify-center overflow-visible">

                {/* Character - The "Presence" */}
                <img
                    src={currentSprite}
                    alt="The Guide"
                    className="absolute z-20 max-w-none object-contain object-bottom"
                    style={{
                        width: '536px',
                        bottom: '4px',
                        left: '50%',
                        transform: 'translateX(-50%) translateY(-5%)',
                        opacity: opacityDip ? 0.7 : (isTierChange ? 0 : 1),
                        filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.95))',
                        transition: 'opacity 150ms ease-in-out, filter 150ms ease-in-out, transform 150ms ease-in-out'
                    }}
                />

                {/* Depth Wash - Fades the feet into the dungeon dark */}
                <div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[536px] h-32 z-30 pointer-events-none"
                    style={{
                        background: 'linear-gradient(to top, hsl(240, 10%, 4%) 0%, transparent 100%)'
                    }}
                />
            </div>
        </div>
    );
}

// ============================================================================
// CHAT POPUP logic
// ============================================================================
function ChatPopup() {
    const { isOpen, tabs, activeTabId, closeChat, setActiveTab, createTab, closeTab, addMessage, updateLastMessage, getActiveTab } = useChat();
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [characterMood, setCharacterMood] = useState('welcome');
    const [currentTier, setCurrentTier] = useState(() => localStorage.getItem('guide_tier') || 't4');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const activeTab = getActiveTab();



    useEffect(() => {
        if (!activeTab?.messages?.length) setCharacterMood('welcome');
        else if (isLoading) setCharacterMood('thinking');
        else {
            const lastRole = activeTab.messages[activeTab.messages.length - 1]?.role;
            setCharacterMood(lastRole === 'assistant' ? 'explaining' : 'neutral');
        }
    }, [activeTab?.messages, isLoading]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [tabs, activeTabId]);

    const handleSendMessage = async () => {
        if (!input.trim() || isLoading) return;
        const msg = input.trim();
        setInput('');
        setIsLoading(true);
        addMessage({ role: 'user', content: msg });
        addMessage({ role: 'assistant', content: '', loading: true });

        try {
            const res = await fetch(`${API_BASE}/api/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: msg, history: activeTab.messages.slice(-10) })
            });
            const data = await res.json();
            updateLastMessage({ content: data.message, loading: false });
        } catch (err) {
            updateLastMessage({ content: 'Deepest apologies, the archives are unresponsive.', loading: false, error: true });
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed bottom-6 right-6 w-[700px] h-[650px] bg-charcoal-950 border border-wine-900/30 shadow-2xl z-[1001] overflow-visible">

            {/* LAYER 0: THE STAGE (Visuals) */}
            <Stage mood={characterMood} tier={currentTier} />

            {/* LAYER 1: THE UI (Interactions) */}
            <div className="relative z-50 flex h-full w-full overflow-visible">

                {/* 1. HUD & Character Info Overlay */}
                <div className="w-52 h-full flex-shrink-0 relative overflow-visible flex flex-col pointer-events-none">
                    <div className="p-6 text-center pointer-events-auto bg-gradient-to-b from-charcoal-950 via-charcoal-950/60 to-transparent">
                        <p className="text-[10px] text-wine-400/50 uppercase tracking-[0.25em] mb-3 font-light">Experience Level</p>
                        <div className="flex items-center justify-center gap-2 bg-wine-950/70 border border-wine-900/50 rounded px-2 py-1.5 shadow-lg">
                            <button onClick={() => {
                                const tiers = ['t1', 't2', 't3', 't4', 't5'];
                                setCurrentTier(tiers[(tiers.indexOf(currentTier) + 4) % 5]);
                            }} className="p-0.5 text-wine-400/60 hover:text-wine-300 transition-colors"><ChevronLeft size={16} /></button>
                            <span className="text-[11px] font-bold text-cream/90 uppercase tracking-wider px-2">{TIER_NAMES[currentTier]}</span>
                            <button onClick={() => {
                                const tiers = ['t1', 't2', 't3', 't4', 't5'];
                                setCurrentTier(tiers[(tiers.indexOf(currentTier) + 1) % 5]);
                            }} className="p-0.5 text-wine-400/60 hover:text-wine-300 transition-colors"><ChevronRight size={16} /></button>
                        </div>
                    </div>
                </div>

                {/* 2. Interaction Panel (Consultee Area) */}
                <div className="flex-1 flex flex-col bg-charcoal-950/85 backdrop-blur-sm border-l border-wine-900/20">
                    {/* Header */}
                    <div className="h-14 border-b border-wine-900/20 flex items-center justify-between px-6">
                        <div className="flex items-center gap-3">
                            <FileText size={16} className="text-wine-500/70" />
                            <span className="text-sm font-semibold text-cream/80 tracking-wide">Ask About Anything</span>
                        </div>
                        <button onClick={closeChat} className="text-gray-600 hover:text-white transition-colors"><X size={18} /></button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-wine-900/40">
                        {!activeTab?.messages?.length ? (
                            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-30">
                                <BookOpen size={48} strokeWidth={1} />
                                <p className="font-serif italic text-lg">Pleasance Guidebook</p>
                            </div>
                        ) : (
                            activeTab.messages.map((m, i) => (
                                <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                                    <div className={`max-w-[90%] p-3 rounded-lg text-sm leading-relaxed ${m.role === 'user' ? 'bg-wine-900/20 text-cream italic border-r-2 border-wine-500/40' : 'text-gray-300'}`}>
                                        {m.loading ? <Pulse>Consulting the index...</Pulse> : (
                                            <ReactMarkdown components={{
                                                p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                                                strong: ({ node, ...props }) => <span className="text-wine-400 font-bold" {...props} />
                                            }}>{m.content}</ReactMarkdown>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Capture */}
                    <div className="p-4 border-t border-wine-900/20 bg-charcoal-950">
                        <div className="relative bg-black/40 border border-wine-900/30 rounded px-3 py-2 flex items-center gap-3">
                            <Feather size={16} className="text-wine-500/40 flex-shrink-0" />
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                                placeholder="Whisper your inquiry..."
                                className="bg-transparent text-sm text-cream w-full focus:outline-none placeholder-gray-700"
                                disabled={isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChatPopup;
